// ── Formatting + small data helpers ──

export const fmt = n => "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtR = n => <span style={{ display:"block", textAlign:"right", fontVariantNumeric:"tabular-nums" }}>${Number(n).toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 })}</span>;

// PERCENT UNITS in, e.g. fmtPerc(5.5) -> "5.50%".
// ACE stores some rates as percent units (Terms.margin = 5.5) and others as
// decimals (advance_rate = 0.85). Passing a decimal here silently renders
// "0.85%" instead of "85.00%" — use fmtRatio for those.
export const fmtPerc = n => Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })+"%";

// DECIMAL RATIO in, e.g. fmtRatio(0.85) -> "85.00%". Prefer this over
// fmtPerc(x * 100) so the units are stated at the call site.
//
// Fixed at 2dp by default, unlike fmtPerc. A ratio carries as many decimals
// as the division happens to produce, and "38.4584%" reads as precision the
// figure does not have. Pass dp to widen it where the extra digits are real.
export const fmtRatio = (n, dp = 2) =>
  Number(Number(n) * 100).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp }) + "%";

export const fmtPhone = p => {
  if (!p) return "—";
  const d = p.toString().replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0,3)})-${d.slice(3,6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === "1") return `(${d.slice(1,4)})-${d.slice(4,7)}-${d.slice(7)}`;
  return p; // return original if not a standard US number
};

export const fmtDate = d => {
  if (!d) return "—";
  const [y, m, day] = d.slice(0, 10).split("-");
  return `${m}/${day}/${y.slice(2)}`;
};

export const now = () => new Date().toISOString().slice(0,16).replace("T"," ");

// Renders a SQL DATE without letting the browser's timezone shift it.
export const wfDate = (d) => {
  if (!d) return "";
  const [y, m, dd] = String(d).slice(0, 10).split("-");
  return `${Number(m)}/${Number(dd)}/${y}`;   // 7/10/2026
};

// ── localFilter ──────────────────────────────────────────────────────────────
// Client-side search behind every SearchBar in the app. Three behaviors the
// old one-liner didn't have, all of them things people actually type:
//
//  1. MULTI-TERM, AND'd across fields. "john smith" used to match nothing,
//     because firstname and lastname are separate columns and neither one
//     contains the whole string. Now every whitespace-separated term has to
//     match SOME field, so a full name works, and so does "smith acme".
//
//  2. PHONE NUMBERS BY DIGITS. "(312) 555-1234", "312-555-1234" and
//     "3125551234" are the same number, so a term of 3+ digits is also
//     compared digits-to-digits. Typing a number the way it's on your phone
//     finds the record however it happens to be punctuated in the database.
//
//  3. DERIVED FIELDS. A field may be a function of the row, for anything that
//     isn't a plain column: fields: ["email", r => r.first + " " + r.last].
//
// Narrowing is impossible here — anything the old version matched, this still
// matches, because a single-term query is the old behavior exactly.
const NON_DIGIT = /\D+/g;

const fieldValue = (row, f) => {
  const v = typeof f === "function" ? f(row) : row[f];
  return v === null || v === undefined ? "" : String(v);
};

export const localFilter = (rows, q, fields) => {
  const raw = (q || "").trim();
  if (!raw) return rows;
  const terms = raw.toLowerCase().split(/\s+/).filter(Boolean);

  return rows.filter(r => {
    const vals = fields.map(f => fieldValue(r, f));
    const lower = vals.map(v => v.toLowerCase());
    let digits = null;   // built lazily — most searches never need it

    return terms.every(t => {
      if (lower.some(v => v.includes(t))) return true;
      const td = t.replace(NON_DIGIT, "");
      if (td.length < 3) return false;   // "1" shouldn't match every phone number
      if (digits === null) digits = vals.map(v => v.replace(NON_DIGIT, ""));
      return digits.some(v => v.length >= 3 && v.includes(td));
    });
  });
};

// Every export reports itself (who, which file, how many rows) for Usage →
// Exports. App.js plugs in the call; format.js stays free of the API.
let exportLogger = null;
export const setExportLogger = fn => { exportLogger = typeof fn === "function" ? fn : null; };

export const exportCSV = (rows, cols, filename, fieldFmts={}) => {
  const fmtCell = (v, col) => {
    if (fieldFmts[col]) return fieldFmts[col](v);
    if (v === null || v === undefined || v === "") return "";
    const s = v.toString().trim().replace(/^,+/, "");
    if (/^\d{4}-\d{2}-\d{2}(T.*)?$/.test(s)) {
      const [y, m, d] = s.slice(0, 10).split("-");
      return `${m}/${d}/${y}`;
    }
    return s.replace(/"/g, '""');
  };
  const hdr = cols.join(",");
  const body = rows.map(r => cols.map(c => "\"" + fmtCell(r[c], c) + "\"").join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([hdr+"\n"+body], {type:"text/csv"}));
  a.download = filename; a.click();
  try { if (exportLogger) exportLogger({ filename, rows: (rows || []).length, columns: (cols || []).length }); } catch (e) { /* never blocks the download */ }
};
// Already-built CSV text (e.g. read off an on-screen table). Same download and
// the same export log as exportCSV.
export const downloadCSVText = (text, filename, meta = {}) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], {type:"text/csv"}));
  a.download = filename; a.click();
  try { if (exportLogger) exportLogger({ filename, rows: meta.rows || 0, columns: meta.columns || 0 }); } catch (e) { /* never blocks the download */ }
};
